



import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Offcanvas, Button } from 'react-bootstrap';
import {
    FaBuilding,
    FaTag,
    FaCalendar,
    FaDollarSign,
    FaFileInvoice,
    FaUsers,
    FaExchangeAlt,
    FaHistory,
    FaUsersCog,
    FaShoppingCart,
    FaFileContract,
    FaListAlt
} from 'react-icons/fa';
import { BiPencil } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import ProjectView from './ProjectView';
import ProjectList from './ProjectList';
import CommunicationDetails from './CommunicationDetails';
import ProjectTeam from './ProjectTeam';
import ProjectSignoff from '../projects/ProjectSignoff';
import ProjectWorkflow from '../projects/ProjectWorkflow';
import QuotationList from '../quotation/QuotationList';
import SelectedContact from '../contact/SelectedContact';
import ProformaInvoiceList from '../ProformaInvoice/ProformaInvoiceList';
import withPermission from '../../utils/withPermission';
import ExpenseList from './expense/ExpenseList';
import ProjectModal from './ProjectModal';

// Fixed: Moved permission wrappers outside to prevent remounting on every render
const WithSubPermission = withPermission(({ children }) => children, ['project-list']);
const WithCommuniPermission = withPermission(({ children }) => children, ['communication-list']);
const WithQuoPermission = withPermission(({ children }) => children, ['quotation-list']);
const WithContactsPermission = withPermission(({ children }) => children, ['contact-list']);
const WithExpensePermission = withPermission(({ children }) => children, ['expense-list']);

const ProjectDetails = ({ show, onHide, projectId, parentId }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get("activeTab") || "subprojectList";

    const [projectDetails, setProjectDetails] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [quotationHistory, setQuotationHistory] = useState([]);
    const [isSales, setIsSales] = useState(false);
    const [isSubproject, setIsSubproject] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [clients, setClients] = useState([]);
    const [projectOrders, setProjectOrders] = useState([]);
    const [projectVendorPOs, setProjectVendorPOs] = useState([]);
    const [isMinimizing, setIsMinimizing] = useState(false);
    const isMinimizingRef = useRef(false);
    const [localShow, setLocalShow] = useState(show);
    const [forceShowContactModal, setForceShowContactModal] = useState(false);

    // Sync ref with state
    useEffect(() => {
        isMinimizingRef.current = isMinimizing;
    }, [isMinimizing]);

    // Sync with parent show prop
    useEffect(() => {
        setLocalShow(show);
    }, [show]);

    // Fetch project details
    const fetchProjectDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/projects/${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            console.log('PROJECT FETCHED:', {
                id: currentId,
                status: response.status,
                data: response.data.Data
            });

            const data = response.data.Data;
            setProjectDetails(data);
            setIsSubproject(!!data.parent_id);
            setIsSales(!!data.issales);
        } catch (error) {
            console.error('Error fetching project details:', error);
            if (error.response) {
                console.log('?? ERROR RESPONSE:', error.response.data);
            }
        }
    };

    // Fetch quotation history
    const fetchQuotationHistory = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/quotations/${currentId}/quotation-history`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setQuotationHistory(response.data.data || []);
        } catch (error) {
            console.error('Error fetching quotation history:', error);
        }
    };

    // Fetch clients
    const fetchClients = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setClients(response.data.Data || []);
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    // Fetch project orders
    const fetchProjectOrders = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/deals?project_id=${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            // Handle various response structures (paginated, direct key, or direct data)
            const ordersData = 
                response.data.deals?.data || 
                response.data.deals || 
                response.data.data || 
                (Array.isArray(response.data) ? response.data : []);
                
            setProjectOrders(ordersData);
        } catch (error) {
            console.error('Error fetching project orders:', error);
        }
    };

    // Fetch project vendor POs
    const fetchProjectVendorPOs = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/purchase-order?project_id=${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            // Handle various response structures (paginated, direct key, or direct data)
            const posData = 
                response.data.purchaseorders?.data || 
                response.data.purchaseorders || 
                response.data.data || 
                (Array.isArray(response.data) ? response.data : []);

            setProjectVendorPOs(posData);
        } catch (error) {
            console.error('Error fetching project vendor POs:', error);
        }
    };

    // Edit project function
    const editProject = async (projectId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            console.log('SENDING PROJECT:', {
                id: projectId,
                payload: updatedData instanceof FormData ? Object.fromEntries(updatedData.entries()) : updatedData
            });

            const response = await axios.post(
                `${backendUrl}/api/projects/${projectId}?_method=PUT`,
                updatedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                        'Tenant-Origin': origin,
                    },
                }
            );

            console.log('UPDATE RESPONSE:', {
                status: response.status,
                updatedData: response.data.Data
            });

            const updatedProject = response.data.Data;

            if (updatedProject) {
                setProjectDetails((prevProject) => ({
                    ...prevProject,
                    ...updatedProject,
                }));
            }

            setShowModal(false);
        } catch (error) {
            console.log('?? ERROR UPDATING PROJECT ON SERVER:', error.response?.data || error.message);
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            } else {
                console.error('Error updating project:', error);
            }
        }
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Format action helper
    const formatAction = (action) => {
        if (!action) return '';
        return action
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useEffect(() => {
        if (localShow && (projectId || id)) {
            console.log('INITIALIZING PROJECT DETAILS:', { projectId, id, localShow });
            setProjectDetails(null);
            fetchProjectDetails();
            fetchClients();
        }
    }, [localShow, projectId, id]);

    useEffect(() => {
        if (localShow && activeTab === 'quotationHistory') {
            fetchQuotationHistory();
        }
        if (localShow && activeTab === 'orders') {
            fetchProjectOrders();
        }
        if (localShow && activeTab === 'vendorPOs') {
            fetchProjectVendorPOs();
        }
    }, [localShow, activeTab]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleOffcanvasClose = () => {
        setProjectDetails(null);
        setActiveTab('subprojectList');
        onHide();
    };

    // Callback functions for temporarily hiding the offcanvas
    const temporarilyHideOffcanvas = () => {
        setIsMinimizing(true);
    };

    const reopenOffcanvas = () => {
        setIsMinimizing(false);
    };

    // const shouldShowOffcanvas = show && !isMinimizing;

    return (
        <Offcanvas
            show={localShow}
            onHide={() => {
                if (!isMinimizingRef.current) handleOffcanvasClose();
            }}
            placement="end"
            style={{ 
                width: '1250px', 
                zIndex: 1045,
                ...(isMinimizing ? { visibility: 'hidden', opacity: 0, pointerEvents: 'none' } : {})
            }}
            backdrop={!isMinimizing}
            backdropClassName={isMinimizing ? 'd-none' : ''}
            className={`Project-offcanvas ${isMinimizing ? 'minimized' : ''}`}
            enforceFocus={false}
            autoFocus={false}
            restoreFocus={false}
            unmountOnExit={false}
        >
            {/* Offcanvas Header */}
            <Offcanvas.Header className="border-bottom bg-white py-3">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle p-2 me-3">
                            <FaBuilding className="text-white" size={18} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">{projectDetails?.name}</h5>
                            <small className="text-muted">ID: {projectDetails?.code || projectId || id}</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                                setSelectedProject(projectDetails);
                                setShowModal(true);
                            }}
                        >
                            <BiPencil className="me-1" />
                            Edit
                        </Button>
                        <Button
                            variant="light"
                            size="sm"
                            onClick={handleOffcanvasClose}
                            className="border-0"
                        >
                            <BsX size={20} />
                        </Button>
                    </div>
                </div>
            </Offcanvas.Header>

            {/* Offcanvas Body */}
            <Offcanvas.Body className="p-0">
                {/* Project Summary Card */}
                <div className="p-4 border-bottom bg-light">
                    <div className="row g-4">
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaTag className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Project Code</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.code || 'Not assigned'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaCalendar className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Start Date</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {formatDate(projectDetails?.start_date) || 'Not specified'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaDollarSign className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Expenditure Amount</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.expenditure_amount || '0.00'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaUsers className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Client</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.client?.name || 'Not assigned'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="border-bottom bg-white">
                    <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                        {!isSales && !isSubproject && (
                            <WithSubPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'subprojectList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('subprojectList')}
                                        role="tab"
                                    >
                                        <FaBuilding className="me-2" size={14} />
                                        Sub Projects
                                    </button>
                                </li>
                            </WithSubPermission>
                        )}

                        <WithQuoPermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotationList' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotationList')}
                                    role="tab"
                                >
                                    <FaFileInvoice className="me-2" size={14} />
                                    Quotations
                                </button>
                            </li>
                        </WithQuoPermission>

                        {/* <WithQuoPermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotationHistory' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotationHistory')}
                                    role="tab"
                                >
                                    <FaHistory className="me-2" size={14} />
                                    Quotation History
                                </button>
                            </li>
                        </WithQuoPermission> */}

                        <WithExpensePermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'expense' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('expense')}
                                    role="tab"
                                >
                                    Expenses
                                </button>
                            </li>
                        </WithExpensePermission>

                        {!isSubproject && (
                            <WithCommuniPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'communication' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('communication')}
                                        role="tab"
                                    >
                                        Communications
                                    </button>
                                </li>
                            </WithCommuniPermission>
                        )}

                        {!isSales && (
                            <WithContactsPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'contactList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('contactList')}
                                        role="tab"
                                    >
                                        <FaUsers className="me-2" size={14} />
                                        Contacts
                                    </button>
                                </li>
                            </WithContactsPermission>
                        )}

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'projectTeam' ? 'active' : ''}`}
                                onClick={() => handleTabClick('projectTeam')}
                                role="tab"
                            >
                                <FaUsersCog className="me-2" size={14} />
                                Project Team
                            </button>
                        </li>

                        {/* <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'signoffs' ? 'active' : ''}`}
                                onClick={() => handleTabClick('signoffs')}
                                role="tab"
                            >
                                ✅ Sign-offs
                            </button>
                        </li>

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'workflow' ? 'active' : ''}`}
                                onClick={() => handleTabClick('workflow')}
                                role="tab"
                            >
                                🔄 Workflow
                            </button>
                        </li> */}

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
                                onClick={() => handleTabClick('orders')}
                                role="tab"
                            >
                                <FaShoppingCart className="me-2" size={14} />
                                Orders
                            </button>
                        </li>

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'vendorPOs' ? 'active' : ''}`}
                                onClick={() => handleTabClick('vendorPOs')}
                                role="tab"
                            >
                                <FaFileContract className="me-2" size={14} />
                                Vendor POs
                            </button>
                        </li>

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'projectDetails' ? 'active' : ''}`}
                                onClick={() => handleTabClick('projectDetails')}
                                role="tab"
                            >
                                Project Details
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Tab Content */}
                <div className="tab-content p-4 bg-white" style={{ minHeight: '500px', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                    {/* Sub Projects Tab */}
                    {activeTab === "subprojectList" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectList
                                clientId={projectDetails?.client_id}
                                parentId={projectId || id}
                                onCloseOffcanvas={temporarilyHideOffcanvas}
                                onReopenOffcanvas={reopenOffcanvas}
                            />
                        </div>
                    )}

                    {/* Quotations Tab */}
                    {activeTab === "quotationList" && projectDetails && (
                        <div className="tab-pane active">
                            <QuotationList
                                projectId={projectId || id}
                                clientId={projectDetails?.client_id || projectDetails?.client?.id}
                                projectName={projectDetails?.name}
                                clientName={projectDetails?.client?.name}
                                clientCurrency={projectDetails?.client?.currency}
                                projectData={projectDetails}
                                setProjectDetails={setProjectDetails}
                            />
                        </div>
                    )}

                    {/* Quotation History Tab */}
                    {activeTab === "quotationHistory" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Date</th>
                                            <th scope="col">Quotation ID</th>
                                            <th scope="col">Action</th>
                                            <th scope="col">Description</th>
                                            <th scope="col">Document</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotationHistory.map((quotation, index) => (
                                            quotation.history && quotation.history.map((historyItem, idx) => (
                                                <tr key={`${index}-${idx}`}>
                                                    <td>{idx + 1}</td>
                                                    <td>{formatDate(quotation.date)}</td>
                                                    <td>{quotation.ref_no}</td>
                                                    <td>{formatAction(historyItem.action)}</td>
                                                    <td>{historyItem.description}</td>
                                                    <td>
                                                        {historyItem.attachment ? (
                                                            <a
                                                                href={`${process.env.REACT_APP_BACKEND_URL}/storage/${historyItem.attachment}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-primary"
                                                            >
                                                                View
                                                            </a>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))
                                        ))}
                                        {quotationHistory.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4 text-muted">
                                                    No quotation history available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Expenses Tab */}
                    {activeTab === "expense" && projectDetails && (
                        <div className="tab-pane active">
                            <ExpenseList
                                projectId={projectId || id}
                                projectDetails={projectDetails}
                            />
                        </div>
                    )}

                    {/* Communications Tab */}
                    {activeTab === "communication" && projectDetails && (
                        <div className="tab-pane active">
                            <CommunicationDetails
                                projectId={projectId || id}
                            />
                        </div>
                    )}

                    {/* Contacts Tab */}
                    {activeTab === "contactList" && projectDetails && (
                        <div className="tab-pane active">
                            <SelectedContact
                                projectId={projectDetails?.id || projectId || id}
                                entityType="project"
                                fetchProjectData={fetchProjectDetails}
                                projectData={projectDetails}
                                setProjectDetails={setProjectDetails}
                                onCloseOffcanvas={temporarilyHideOffcanvas}
                                onReopenOffcanvas={reopenOffcanvas}
                                showContactModal={forceShowContactModal}
                                setShowContactModal={setForceShowContactModal}
                            />
                        </div>
                    )}

                    {/* Project Team Tab */}
                    {activeTab === "projectTeam" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectTeam 
                                projectId={projectDetails?.id || projectId || id}
                                projectData={projectDetails}
                                fetchProjectDetails={fetchProjectDetails}
                                onAddContact={() => setForceShowContactModal(true)}
                                onCloseParent={onHide} 
                                onMinimizeParent={temporarilyHideOffcanvas}
                                onReopenParent={reopenOffcanvas}
                            />
                        </div>
                    )}

                    {/* Sign-offs Tab */}
                    {activeTab === "signoffs" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectSignoff
                                projectId={projectDetails?.id || projectId || id}
                            />
                        </div>
                    )}

                    {/* Workflow Tab */}
                    {activeTab === "workflow" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectWorkflow
                                projectId={projectDetails?.id || projectId || id}
                                initialStage={projectDetails?.workflow_stage || 'planning'}
                            />
                        </div>
                    )}

                    {/* Project Details Tab */}
                    {activeTab === "projectDetails" && (
                        <div className="tab-pane active">
                            {projectDetails ? (
                                <ProjectView
                                    data={projectDetails}
                                    setProjectDetails={setProjectDetails}
                                />
                            ) : (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading project details...</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Orders Tab */}
                    {activeTab === "orders" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">Project Orders</h6>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle font-size-13 custom-table shadow-sm rounded">
                                    <thead className="bg-light-subtle border-bottom">
                                        <tr>
                                            <th className="py-3" style={{ width: '50px' }}>#</th>
                                            <th className="py-3">PO Number</th>
                                            <th className="py-3">Issue Date</th>
                                            <th className="py-3">Deal Name</th>
                                            <th className="py-3 text-end">Order Value</th>
                                            <th className="py-3 text-end">Recurring Cost</th>
                                            <th className="py-3 text-end">One-Time Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody className="border-0">
                                        {projectOrders.filter(d => d.stage === 'order').map((deal, index) => (
                                            <tr key={`deal-${deal.id}`} className="border-bottom-0">
                                                <td className="text-muted">{index + 1}</td>
                                                <td className="fw-medium text-primary">{deal.po_number || deal.external_id}</td>
                                                <td className="text-muted">{deal.issue_date ? formatDate(deal.issue_date) : '-'}</td>
                                                <td>
                                                    <div className="text-muted" style={{ fontSize: '11px' }}>{deal.name}</div>
                                                </td>
                                                <td className="text-end fw-bold">{parseFloat(deal.po_value || deal.value || 0).toLocaleString()}</td>
                                                <td className="text-end text-primary">{parseFloat(deal.recurring_cost || 0).toLocaleString()}</td>
                                                <td className="text-end">{parseFloat(deal.one_time_cost || 0).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        {projectOrders.filter(d => d.stage === 'order').length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="text-muted mb-2">No project orders found.</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Vendor POs Tab */}
                    {activeTab === "vendorPOs" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">Vendor Purchase Orders</h6>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle font-size-13 custom-table shadow-sm rounded">
                                    <thead className="bg-light-subtle border-bottom">
                                        <tr>
                                            <th className="py-3" style={{ width: '50px' }}>#</th>
                                            <th className="py-3">PO Reference</th>
                                            <th className="py-3">Issue Date</th>
                                            <th className="py-3">Vendor</th>
                                            <th className="py-3 text-end">Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="border-0">
                                        {projectVendorPOs.map((po, index) => (
                                            <tr key={`po-${po.id}`} className="border-bottom-0">
                                                <td className="text-muted">{index + 1}</td>
                                                <td className="fw-medium text-info">{po.ref_no}</td>
                                                <td className="text-muted">{po.date ? formatDate(po.date) : '-'}</td>
                                                <td>
                                                    <div className="text-dark fw-medium small">{po.vendor?.name}</div>
                                                </td>
                                                <td className="text-end fw-bold">{parseFloat(po.total_amount || po.amount || 0).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        {projectVendorPOs.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <div className="text-muted mb-2">No vendor purchase orders found.</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Offcanvas.Body>

            {/* Project Modal */}
            {showModal && (
                <ProjectModal
                    showModal={showModal}
                    handleClose={() => setShowModal(false)}
                    projectId={projectId || id}
                    projectData={selectedProject}
                    editProject={editProject}
                    clients={clients}
                    fetchClients={fetchClients}
                    defaultClientId={selectedProject?.client_id}
                />
            )}
        </Offcanvas>
    );
};

export default ProjectDetails;
